import { useContext, useEffect } from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import { Grid, ImageList } from "@mui/material";
import { ClaimContext } from "../../context/claimContext/ClaimContext";
import { AuthContext } from "../../context/authContext/AuthContext";
import { getClaim } from "../../context/claimContext/ClaimApiCalls";
import ImageComponent from "../../components/imageComponent/ImageComponent";
import LoadingCircle from "../../components/loadingCircle/LoadingCircle";

const Container = styled.div`
  flex: 4;
  padding: 0px 20px 20px 20px;
`;

const Center = styled.div`
  margin: auto;
`;

const Subtitle = styled.h3`
  margin-top: 20px;
  margin-bottom: 5px;
`;

const PropertyContainer = styled.div`
  margin-left: 5px;
  padding: 5px;
  word-wrap: break-word;
`;

const PropertyLabel = styled.label`
  color: #434446;
  font-weight: 600;
  margin-bottom: 2px;
`;

const PropertyContent = styled.p`
  margin-top: 5px;
  border-bottom: 1px solid gray;
`;

const Claim = () => {
  const { claimId } = useParams();
  const { claims, isFetching, error, dispatch } = useContext(ClaimContext);
  const { user } = useContext(AuthContext);

  useEffect(() => {
    getClaim(claimId, dispatch);
  }, [claimId, dispatch]);

  if (
    isFetching ||
    claims.length < 1 ||
    claims[0].attachmentDocuments === undefined
  ) {
    return (
      <Center>
        <LoadingCircle />
      </Center>
    );
  }

  if (error) {
    return <div>error...</div>;
  }

  return (
    <Container>
      <Grid container spacing={10}>
        <Grid item xs={12} sm={6}>
          <Subtitle>Order Details</Subtitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <PropertyContainer>
                <PropertyLabel>Order Number</PropertyLabel>
                <PropertyContent>{claims[0].orderNumber}</PropertyContent>
              </PropertyContainer>
            </Grid>
            <Grid item xs={12} sm={6}>
              <PropertyContainer>
                <PropertyLabel>Order Reference:</PropertyLabel>
                <PropertyContent>{claims[0].orderReference}</PropertyContent>
              </PropertyContainer>
            </Grid>
          </Grid>
          <Subtitle>End-customer Details</Subtitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <PropertyContainer>
                <PropertyLabel>Location:</PropertyLabel>
                <PropertyContent>{claims[0].installLocation}</PropertyContent>
              </PropertyContainer>
            </Grid>
            <Grid item xs={12} sm={6}>
              <PropertyContainer>
                <PropertyLabel>Contact Person:</PropertyLabel>
                <PropertyContent>
                  {claims[0].endCustomerContact}
                </PropertyContent>
              </PropertyContainer>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <PropertyContainer>
                <PropertyLabel>Email Address:</PropertyLabel>
                <PropertyContent>{claims[0].endCustomerEmail}</PropertyContent>
              </PropertyContainer>
            </Grid>
            <Grid item xs={12} sm={6}>
              <PropertyContainer>
                <PropertyLabel>Phone number:</PropertyLabel>
                <PropertyContent>{claims[0].endCustomerPhone}</PropertyContent>
              </PropertyContainer>
            </Grid>
          </Grid>
          <Subtitle>Claim Details</Subtitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <PropertyContainer>
                <PropertyLabel>Claim Number:</PropertyLabel>
                <PropertyContent>{claims[0].caseNumber}</PropertyContent>
              </PropertyContainer>
            </Grid>
            <Grid item xs={12} sm={4}>
              <PropertyContainer>
                <PropertyLabel>Affected positions:</PropertyLabel>
                <PropertyContent>{claims[0].position}</PropertyContent>
              </PropertyContainer>
            </Grid>
            <Grid item xs={12} sm={4}>
              <PropertyContainer>
                <PropertyLabel>Status:</PropertyLabel>
                <PropertyContent>{claims[0].status}</PropertyContent>
              </PropertyContainer>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <PropertyContainer>
                <PropertyLabel>Time of Claim Registration (UTC):</PropertyLabel>
                <PropertyContent>{claims[0].createdDateUtc}</PropertyContent>
              </PropertyContainer>
            </Grid>
            <Grid item xs={12} sm={6}>
              <PropertyContainer>
                <PropertyLabel>Time of Claim close (UTC)::</PropertyLabel>
                <PropertyContent>
                  {claims[0].closedDateUtc === "0001-01-01 00:00"
                    ? "-"
                    : claims[0].closedDateUtc}
                </PropertyContent>
              </PropertyContainer>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12}>
            <PropertyContainer>
              <PropertyLabel>Problem description:</PropertyLabel>
              <PropertyContent>{claims[0].description}</PropertyContent>
            </PropertyContainer>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <PropertyContainer>
                <PropertyLabel>Damage noticed at:</PropertyLabel>
                <PropertyContent>
                  {claims[0].damageNoticeLocation}
                </PropertyContent>
              </PropertyContainer>
            </Grid>
            <Grid item xs={12} sm={4}>
              <PropertyContainer>
                <PropertyLabel>Accessories Defects:</PropertyLabel>
                <PropertyContent>
                  {claims[0].accessoriesDefects}
                </PropertyContent>
              </PropertyContainer>
            </Grid>
            <Grid item xs={12} sm={4}>
              <PropertyContainer>
                <PropertyLabel>Fitting Defects:</PropertyLabel>
                <PropertyContent>{claims[0].fittingDefects}</PropertyContent>
              </PropertyContainer>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <PropertyContainer>
                <PropertyLabel>Unit Defects:</PropertyLabel>
                <PropertyContent>{claims[0].glassDefects}</PropertyContent>
              </PropertyContainer>
            </Grid>
            <Grid item xs={12} sm={4}>
              <PropertyContainer>
                <PropertyLabel>Glass Defects:</PropertyLabel>
                <PropertyContent>{claims[0].unitDefects}</PropertyContent>
              </PropertyContainer>
            </Grid>
            <Grid item xs={12} sm={4}>
              <PropertyContainer>
                <PropertyLabel>Damaged Glass:</PropertyLabel>
                <PropertyContent>{claims[0].damagedGlass}</PropertyContent>
              </PropertyContainer>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Subtitle>Attachments:</Subtitle>
          <Grid item xs={12} sm={12}>
            <ImageList sx={{ height: 280 }} cols={4} rowHeight={120}>
              {claims[0].attachmentDocuments.map((document) => (
                <ImageComponent
                  key={document.attachmentId}
                  alt={document.name}
                  fileName={claims[0].caseNumber + "." + document.fileExtention}
                  extention={document.fileExtention}
                  accountId={user.salesforceAccountId}
                  claimId={claimId}
                  documentId={document.attachmentId}
                />
              ))}
            </ImageList>
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Claim;
